/**
 * PositionEditDialog - Slide-over panel for creating/editing position (JD) details
 * 90% viewport width, slides in from right with backdrop overlay.
 *
 * Auto-save behaviour:
 *   CREATE mode – position is auto-created when the Role field is filled and blurred.
 *   EDIT mode   – every field change is debounced (800 ms) and persisted automatically.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TeamPosition, TeamCandidate, PositionPriority, PositionStatus } from '../../types/team';
import { JDExportModal } from './JDExportModal';
import { JDImportModal } from './JDImportModal';
import { InfographicModal } from './InfographicModal';
import { pickFileFromDrive } from '../../services/googleDrivePicker';

// ============================================================================
// Constants / Enums
// ============================================================================

const PRIORITY_OPTIONS: { value: PositionPriority; label: string; color: string }[] = [
  { value: 'critical', label: 'Critical', color: 'bg-red-600 text-white' },
  { value: 'important', label: 'Important', color: 'bg-yellow-600 text-white' },
  { value: 'nice-to-have', label: 'Nice to Have', color: 'bg-gray-600 text-gray-200' },
];

const STATUS_OPTIONS: { value: PositionStatus; label: string }[] = [
  { value: 'proposed', label: 'Proposed' },
  { value: 'confirmed', label: 'Confirmed' },
];

const WORK_MODEL_OPTIONS = ['onsite', 'remote', 'hybrid'] as const;

const EXPERIENCE_LEVELS = [
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Mid' },
  { value: 'senior', label: 'Senior' },
  { value: 'staff', label: 'Staff' },
  { value: 'principal', label: 'Principal' },
  { value: 'lead', label: 'Lead' },
];

const MANAGEMENT_LEVELS = [
  { value: 'ic', label: 'Individual Contributor' },
  { value: 'team_lead', label: 'Team Lead' },
  { value: 'manager', label: 'Manager' },
  { value: 'director', label: 'Director' },
  { value: 'vp', label: 'VP' },
];

const EDUCATION_LEVELS = [
  { value: 'none', label: 'None Required' },
  { value: 'bachelors', label: "Bachelor's" },
  { value: 'masters', label: "Master's" },
  { value: 'phd', label: 'PhD' },
];

const EDUCATION_IMPORTANCE = [
  { value: 'required', label: 'Required' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'nice_to_have', label: 'Nice to Have' },
];

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'freelance', label: 'Freelance' },
];

const RATE_COUNTRIES = ['Czechia', 'Switzerland', 'MENA', 'EU', 'Offshore'];

type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// ============================================================================
// Props
// ============================================================================

export interface PositionEditDialogProps {
  position: TeamPosition | null; // null = create mode
  teamId: number;
  ensureTeamId?: () => Promise<number>;
  onCreate: (data: Partial<TeamPosition>) => Promise<TeamPosition>;
  onUpdate: (positionId: number, data: Partial<TeamPosition>) => Promise<void>;
  onClose: () => void;
  onAIFill?: (input: { role?: string; description?: string }) => Promise<Partial<TeamPosition>>;
  onAIAnalysis?: (positionId: number) => Promise<{ ai_analysis: string }>;
  onInfographicGenerated?: (positionId: number) => void;
}

// ============================================================================
// Component
// ============================================================================

export const PositionEditDialog: React.FC<PositionEditDialogProps> = ({
  position,
  teamId,
  ensureTeamId,
  onCreate,
  onUpdate,
  onClose,
  onAIFill,
  onAIAnalysis,
  onInfographicGenerated,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // JD modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showInfographicModal, setShowInfographicModal] = useState(false);
  const [hasInfographic, setHasInfographic] = useState(!!position?.infographic_base64);

  // Resolved team ID — may be updated lazily via ensureTeamId()
  const [resolvedTeamId, setResolvedTeamId] = useState(teamId);
  useEffect(() => { setResolvedTeamId(teamId); }, [teamId]);

  const getValidTeamId = useCallback(async (): Promise<number> => {
    if (resolvedTeamId > 0) return resolvedTeamId;
    if (ensureTeamId) {
      const id = await ensureTeamId();
      setResolvedTeamId(id);
      return id;
    }
    throw new Error('No team available');
  }, [resolvedTeamId, ensureTeamId]);

  // Tracks the DB id – starts from position prop; updated after auto-create
  const [positionId, setPositionId] = useState<number | null>(position?.id ?? null);
  const isEditMode = positionId !== null;

  // Form state
  const [role, setRole] = useState(position?.role || '');
  const [description, setDescription] = useState(position?.description || '');
  const [countNeeded, setCountNeeded] = useState(position?.count_needed || 1);
  const [priority, setPriority] = useState<PositionPriority>(position?.priority || 'important');
  const [status, setStatus] = useState<PositionStatus>(position?.status || 'proposed');
  const [employmentType, setEmploymentType] = useState(position?.employment_type || '');
  const [allocationPercentage, setAllocationPercentage] = useState(position?.allocation_percentage || 100);

  // Location & Rate
  const [locationModel, setLocationModel] = useState(position?.location_model || '');
  const [location, setLocation] = useState(position?.location || '');
  const [rateCountry, setRateCountry] = useState(position?.rate_country || '');

  // Requirements
  const [experienceLevel, setExperienceLevel] = useState(position?.experience_level || '');
  const [managementLevel, setManagementLevel] = useState(position?.management_level || '');
  const [educationLevel, setEducationLevel] = useState(position?.education_level || '');
  const [educationImportance, setEducationImportance] = useState(position?.education_importance || '');

  // Skills
  const [skillsRequired, setSkillsRequired] = useState<string[]>(position?.skills_required || []);
  const [skillsNiceToHave, setSkillsNiceToHave] = useState<string[]>(position?.skills_nice_to_have || []);
  const [newRequiredSkill, setNewRequiredSkill] = useState('');
  const [newNiceSkill, setNewNiceSkill] = useState('');

  // Responsibilities
  const [responsibilities, setResponsibilities] = useState<string[]>(position?.responsibilities || []);
  const [newResponsibility, setNewResponsibility] = useState('');

  // AI Analysis
  const [aiAnalysis, setAiAnalysis] = useState(position?.ai_analysis || '');
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const analysisTimer = useRef<ReturnType<typeof setTimeout>>();
  const analysisAbort = useRef<AbortController>();

  // Loading / status states
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  const [aiFillingFields, setAiFillingFields] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Track whether any non-role field has been touched (for close confirmation)
  const hasUnsavedFields = useRef(false);

  // ============================================================================
  // Auto-save debounce machinery
  // ============================================================================

  const pendingChanges = useRef<Partial<TeamPosition>>({});
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();
  // Keep latest positionId in a ref so the debounce callback always sees the current value
  const positionIdRef = useRef(positionId);
  useEffect(() => { positionIdRef.current = positionId; }, [positionId]);

  const flushPendingChanges = useCallback(async () => {
    clearTimeout(debounceTimer.current);
    const pid = positionIdRef.current;
    if (!pid) return;
    const toSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    if (Object.keys(toSave).length === 0) return;
    setAutoSaveStatus('saving');
    try {
      await onUpdate(pid, toSave);
      setAutoSaveStatus('saved');
    } catch {
      setAutoSaveStatus('error');
    }
  }, [onUpdate]);

  const scheduleAutoSave = useCallback((changes: Partial<TeamPosition>) => {
    if (!positionIdRef.current) return; // only auto-save in edit mode
    Object.assign(pendingChanges.current, changes);
    clearTimeout(debounceTimer.current);
    setAutoSaveStatus('idle'); // show unsaved while debouncing
    debounceTimer.current = setTimeout(async () => {
      await flushPendingChanges();
    }, 800);
  }, [flushPendingChanges]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(debounceTimer.current);
      clearTimeout(analysisTimer.current);
      analysisAbort.current?.abort();
    };
  }, []);

  const ANALYSIS_FIELDS = new Set<string>([
    'role', 'description', 'skills_required', 'skills_nice_to_have',
    'responsibilities', 'experience_level', 'employment_type',
  ]);

  const scheduleAnalysis = useCallback((changedField?: string) => {
    if (!onAIAnalysis) return;
    if (changedField && !ANALYSIS_FIELDS.has(changedField)) return;
    clearTimeout(analysisTimer.current);
    analysisTimer.current = setTimeout(async () => {
      const pid = positionIdRef.current;
      if (!pid) return;
      // Flush pending saves first so analysis sees latest data
      await flushPendingChanges();
      analysisAbort.current?.abort();
      const abort = new AbortController();
      analysisAbort.current = abort;
      setAnalysisLoading(true);
      try {
        const result = await onAIAnalysis(pid);
        if (!abort.signal.aborted) {
          setAiAnalysis(result.ai_analysis);
        }
      } catch (err) {
        if (!abort.signal.aborted) {
          console.error('Background AI analysis failed:', err);
        }
      } finally {
        if (!abort.signal.aborted) {
          setAnalysisLoading(false);
        }
      }
    }, 3000);
  }, [onAIAnalysis, flushPendingChanges]);

  // ============================================================================
  // Field update helpers
  // ============================================================================

  /** Update a field locally + schedule auto-save (edit mode only) */
  const updateField = <K extends keyof TeamPosition>(field: K, value: TeamPosition[K], setter: (v: any) => void) => {
    setter(value);
    hasUnsavedFields.current = true;
    scheduleAutoSave({ [field]: value } as Partial<TeamPosition>);
    scheduleAnalysis(field as string);
  };

  // ============================================================================
  // Auto-create on role blur
  // ============================================================================

  const handleRoleBlur = useCallback(async () => {
    if (positionIdRef.current) return; // already created
    if (!role.trim()) return;
    setIsCreating(true);
    try {
      const data: Partial<TeamPosition> = {
        role: role.trim(),
        description: description.trim() || undefined,
        count_needed: countNeeded,
        priority,
        status,
        employment_type: employmentType || undefined,
        allocation_percentage: allocationPercentage,
        location_model: locationModel || undefined,
        location: location.trim() || undefined,
        rate_country: rateCountry || undefined,
        experience_level: experienceLevel || undefined,
        management_level: managementLevel || undefined,
        education_level: educationLevel || undefined,
        education_importance: educationImportance || undefined,
        skills_required: skillsRequired.length > 0 ? skillsRequired : undefined,
        skills_nice_to_have: skillsNiceToHave.length > 0 ? skillsNiceToHave : undefined,
        responsibilities: responsibilities.length > 0 ? responsibilities : undefined,
        ai_analysis: aiAnalysis || undefined,
      };
      const created = await onCreate(data);
      setPositionId(created.id);
      setAutoSaveStatus('saved');
      scheduleAnalysis('role');
    } catch (err) {
      console.error('Auto-create position failed:', err);
      setAutoSaveStatus('error');
    } finally {
      setIsCreating(false);
    }
  }, [
    role, description, countNeeded, priority, status, employmentType, allocationPercentage,
    locationModel, location, rateCountry, experienceLevel, managementLevel, educationLevel,
    educationImportance, skillsRequired, skillsNiceToHave, responsibilities, aiAnalysis,
    onCreate,
  ]);

  // ============================================================================
  // Animation
  // ============================================================================

  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
    return () => setIsVisible(false);
  }, []);

  // ============================================================================
  // Close logic
  // ============================================================================

  const handleClose = useCallback(async () => {
    // Flush any pending auto-save before closing
    await flushPendingChanges();

    // If in create mode (never saved) and user has filled fields but not role → confirm discard
    if (!positionIdRef.current && hasUnsavedFields.current && !role.trim()) {
      const confirmed = window.confirm('Discard unsaved position?');
      if (!confirmed) return;
    }

    setIsVisible(false);
    setTimeout(onClose, 200);
  }, [onClose, role, flushPendingChanges]);

  // Escape key handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleClose]);

  // ============================================================================
  // AI Magic
  // ============================================================================

  const handleAIMagic = async () => {
    const hasInput = role.trim() || description.trim();
    if (!hasInput) return;
    setAiFillingFields(true);
    try {
      // Step 1: Auto-fill empty fields
      if (onAIFill) {
        const input: { role?: string; description?: string } = {};
        if (role.trim()) input.role = role.trim();
        if (description.trim()) input.description = description.trim();
        const suggestions = await onAIFill(input);

        // Collect changes for auto-save
        const changes: Partial<TeamPosition> = {};

        // AI can suggest a role title when only description was provided
        if (suggestions.role && !role.trim()) {
          setRole(suggestions.role);
          changes.role = suggestions.role;
        }
        if (suggestions.description && !description) {
          setDescription(suggestions.description);
          changes.description = suggestions.description;
        }
        if (suggestions.skills_required?.length && skillsRequired.length === 0) {
          setSkillsRequired(suggestions.skills_required);
          changes.skills_required = suggestions.skills_required;
        }
        if (suggestions.skills_nice_to_have?.length && skillsNiceToHave.length === 0) {
          setSkillsNiceToHave(suggestions.skills_nice_to_have);
          changes.skills_nice_to_have = suggestions.skills_nice_to_have;
        }
        if (suggestions.experience_level && !experienceLevel) {
          setExperienceLevel(suggestions.experience_level);
          changes.experience_level = suggestions.experience_level;
        }
        if (suggestions.management_level && !managementLevel) {
          setManagementLevel(suggestions.management_level);
          changes.management_level = suggestions.management_level;
        }
        if (suggestions.education_level && !educationLevel) {
          setEducationLevel(suggestions.education_level);
          changes.education_level = suggestions.education_level;
        }
        if (suggestions.education_importance && !educationImportance) {
          setEducationImportance(suggestions.education_importance);
          changes.education_importance = suggestions.education_importance;
        }
        if (suggestions.employment_type && !employmentType) {
          setEmploymentType(suggestions.employment_type);
          changes.employment_type = suggestions.employment_type;
          if (suggestions.employment_type === 'full_time') {
            setAllocationPercentage(100);
            changes.allocation_percentage = 100;
          }
        }
        if (suggestions.location_model && !locationModel) {
          setLocationModel(suggestions.location_model);
          changes.location_model = suggestions.location_model;
        }
        if (suggestions.responsibilities?.length && responsibilities.length === 0) {
          setResponsibilities(suggestions.responsibilities);
          changes.responsibilities = suggestions.responsibilities;
        }
        if (suggestions.priority && priority === 'important') {
          setPriority(suggestions.priority);
          changes.priority = suggestions.priority;
        }
        const aiEffectiveType = changes.employment_type || employmentType;
        if (aiEffectiveType !== 'full_time' && suggestions.allocation_percentage && allocationPercentage === 100) {
          setAllocationPercentage(suggestions.allocation_percentage);
          changes.allocation_percentage = suggestions.allocation_percentage;
        }

        hasUnsavedFields.current = true;

        // If we now have a role and position isn't created yet, auto-create
        const effectiveRole = suggestions.role && !role.trim() ? suggestions.role : role.trim();
        if (effectiveRole && !positionIdRef.current) {
          setIsCreating(true);
          try {
            const createData: Partial<TeamPosition> = {
              role: effectiveRole,
              description: suggestions.description || description.trim() || undefined,
              count_needed: countNeeded,
              priority: (suggestions.priority as PositionPriority) || priority,
              status,
              employment_type: suggestions.employment_type || employmentType || undefined,
              allocation_percentage: suggestions.allocation_percentage || allocationPercentage,
              location_model: suggestions.location_model || locationModel || undefined,
              location: location.trim() || undefined,
              rate_country: rateCountry || undefined,
              experience_level: suggestions.experience_level || experienceLevel || undefined,
              management_level: suggestions.management_level || managementLevel || undefined,
              education_level: suggestions.education_level || educationLevel || undefined,
              education_importance: suggestions.education_importance || educationImportance || undefined,
              skills_required: (suggestions.skills_required?.length ? suggestions.skills_required : skillsRequired.length > 0 ? skillsRequired : undefined),
              skills_nice_to_have: (suggestions.skills_nice_to_have?.length ? suggestions.skills_nice_to_have : skillsNiceToHave.length > 0 ? skillsNiceToHave : undefined),
              responsibilities: (suggestions.responsibilities?.length ? suggestions.responsibilities : responsibilities.length > 0 ? responsibilities : undefined),
              ai_analysis: aiAnalysis || undefined,
            };
            const created = await onCreate(createData);
            setPositionId(created.id);
            setAutoSaveStatus('saved');
          } catch (err) {
            console.error('Auto-create after AI Magic failed:', err);
            setAutoSaveStatus('error');
          } finally {
            setIsCreating(false);
          }
        } else if (positionIdRef.current && Object.keys(changes).length > 0) {
          // Already in edit mode – schedule save for the changed fields
          scheduleAutoSave(changes);
        }

        if (Object.keys(changes).length > 0) {
          scheduleAnalysis('role');
        }
      }
    } catch (err) {
      console.error('AI Magic failed:', err);
    } finally {
      setAiFillingFields(false);
    }
  };

  // ============================================================================
  // Skill / Responsibility helpers
  // ============================================================================

  const addSkill = (type: 'required' | 'nice', value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (type === 'required') {
      if (!skillsRequired.includes(trimmed)) {
        const next = [...skillsRequired, trimmed];
        setSkillsRequired(next);
        scheduleAutoSave({ skills_required: next });
        scheduleAnalysis('skills_required');
      }
      setNewRequiredSkill('');
    } else {
      if (!skillsNiceToHave.includes(trimmed)) {
        const next = [...skillsNiceToHave, trimmed];
        setSkillsNiceToHave(next);
        scheduleAutoSave({ skills_nice_to_have: next });
        scheduleAnalysis('skills_nice_to_have');
      }
      setNewNiceSkill('');
    }
    hasUnsavedFields.current = true;
  };

  const removeSkill = (type: 'required' | 'nice', index: number) => {
    if (type === 'required') {
      const next = skillsRequired.filter((_, i) => i !== index);
      setSkillsRequired(next);
      scheduleAutoSave({ skills_required: next.length > 0 ? next : [] });
      scheduleAnalysis('skills_required');
    } else {
      const next = skillsNiceToHave.filter((_, i) => i !== index);
      setSkillsNiceToHave(next);
      scheduleAutoSave({ skills_nice_to_have: next.length > 0 ? next : [] });
      scheduleAnalysis('skills_nice_to_have');
    }
    hasUnsavedFields.current = true;
  };

  const addResponsibility = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const next = [...responsibilities, trimmed];
    setResponsibilities(next);
    setNewResponsibility('');
    scheduleAutoSave({ responsibilities: next });
    scheduleAnalysis('responsibilities');
    hasUnsavedFields.current = true;
  };

  const removeResponsibility = (index: number) => {
    const next = responsibilities.filter((_, i) => i !== index);
    setResponsibilities(next);
    scheduleAutoSave({ responsibilities: next.length > 0 ? next : [] });
    scheduleAnalysis('responsibilities');
    hasUnsavedFields.current = true;
  };

  const candidates = position?.candidates || [];

  // ============================================================================
  // JD Import apply handler
  // ============================================================================

  const handleApplyImport = useCallback(async (extracted: Partial<TeamPosition>, mode: 'merge' | 'overwrite') => {
    const changes: Partial<TeamPosition> = {};

    const apply = <K extends keyof TeamPosition>(
      field: K,
      value: TeamPosition[K] | undefined,
      setter: (v: any) => void,
      currentValue: any,
    ) => {
      if (value === undefined || value === null) return;
      if (mode === 'merge' && currentValue && (Array.isArray(currentValue) ? currentValue.length > 0 : true)) return;
      setter(value);
      (changes as any)[field] = value;
    };

    apply('role', extracted.role, setRole, role);
    apply('description', extracted.description, setDescription, description);
    apply('skills_required', extracted.skills_required, setSkillsRequired, skillsRequired);
    apply('skills_nice_to_have', extracted.skills_nice_to_have, setSkillsNiceToHave, skillsNiceToHave);
    apply('responsibilities', extracted.responsibilities, setResponsibilities, responsibilities);
    apply('experience_level', extracted.experience_level, setExperienceLevel, experienceLevel);
    apply('management_level', extracted.management_level, setManagementLevel, managementLevel);
    apply('education_level', extracted.education_level, setEducationLevel, educationLevel);
    apply('education_importance', extracted.education_importance, setEducationImportance, educationImportance);
    apply('employment_type', extracted.employment_type, setEmploymentType, employmentType);
    apply('location_model', extracted.location_model, setLocationModel, locationModel);
    apply('location', extracted.location, setLocation, location);
    const effectiveType = (changes as any).employment_type || employmentType;
    if (effectiveType === 'full_time') {
      setAllocationPercentage(100);
      (changes as any).allocation_percentage = 100;
    } else {
      apply('allocation_percentage', extracted.allocation_percentage, setAllocationPercentage, allocationPercentage);
    }
    apply('priority', extracted.priority, setPriority, priority);
    apply('count_needed', extracted.count_needed, setCountNeeded, countNeeded);

    if (Object.keys(changes).length > 0 && aiAnalysis) {
      setAiAnalysis('');
      (changes as any).ai_analysis = '';
    }

    hasUnsavedFields.current = true;

    // If position not yet created and we have a role, auto-create
    const effectiveRole = (changes.role || role || '').trim();
    if (effectiveRole && !positionIdRef.current) {
      setIsCreating(true);
      try {
        const createData: Partial<TeamPosition> = {
          role: effectiveRole,
          description: (changes.description as string) || description.trim() || undefined,
          count_needed: (changes.count_needed as number) || countNeeded,
          priority: (changes.priority as PositionPriority) || priority,
          status,
          employment_type: (changes.employment_type as string) || employmentType || undefined,
          allocation_percentage: (changes.allocation_percentage as number) || allocationPercentage,
          location_model: (changes.location_model as string) || locationModel || undefined,
          location: (changes.location as string) || location.trim() || undefined,
          rate_country: rateCountry || undefined,
          experience_level: (changes.experience_level as string) || experienceLevel || undefined,
          management_level: (changes.management_level as string) || managementLevel || undefined,
          education_level: (changes.education_level as string) || educationLevel || undefined,
          education_importance: (changes.education_importance as string) || educationImportance || undefined,
          skills_required: (changes.skills_required as string[]) || (skillsRequired.length > 0 ? skillsRequired : undefined),
          skills_nice_to_have: (changes.skills_nice_to_have as string[]) || (skillsNiceToHave.length > 0 ? skillsNiceToHave : undefined),
          responsibilities: (changes.responsibilities as string[]) || (responsibilities.length > 0 ? responsibilities : undefined),
        };
        const created = await onCreate(createData);
        setPositionId(created.id);
        setAutoSaveStatus('saved');
      } catch (err) {
        console.error('Auto-create after JD import failed:', err);
        setAutoSaveStatus('error');
      } finally {
        setIsCreating(false);
      }
    } else if (positionIdRef.current && Object.keys(changes).length > 0) {
      scheduleAutoSave(changes);
    }

    if (Object.keys(changes).length > 0) {
      scheduleAnalysis('role');
    }

    setShowImportModal(false);
  }, [
    role, description, skillsRequired, skillsNiceToHave, responsibilities,
    experienceLevel, managementLevel, educationLevel, educationImportance,
    employmentType, locationModel, location, allocationPercentage, priority, countNeeded,
    status, rateCountry, onCreate, scheduleAutoSave, scheduleAnalysis,
  ]);

  const handleCreateNewFromImport = useCallback(async (extracted: Partial<TeamPosition>) => {
    setShowImportModal(false);
    try {
      setIsCreating(true);
// @ts-ignore
      const created = await onCreate(extracted);
      setIsCreating(false);
      onClose();
      // Parent will re-render the new position from the team data
    } catch (err) {
      console.error('Create new position from JD import failed:', err);
      setIsCreating(false);
      setAutoSaveStatus('error');
    }
  }, [onCreate, onClose]);

  const handleExportClick = useCallback(async () => {
    await flushPendingChanges();
    setShowExportModal(true);
  }, [flushPendingChanges]);

  // Use the position's team_id for API calls so we never send wrong/zero team (fixes 404 "Position not found for this team")
  const teamIdForPosition = (position && position.id === positionId ? position.team_id : undefined) ?? resolvedTeamId;

  // ============================================================================
  // Auto-save status indicator
  // ============================================================================

  const renderStatusIndicator = () => {
    if (isCreating) {
      return <span className="text-xs text-yellow-400 font-medium animate-pulse">Creating...</span>;
    }
    switch (autoSaveStatus) {
      case 'saving':
        return <span className="text-xs text-yellow-400 font-medium animate-pulse">Saving...</span>;
      case 'saved':
        return <span className="text-xs text-green-400 font-medium">Saved</span>;
      case 'error':
        return <span className="text-xs text-red-400 font-medium">Save failed</span>;
      default:
        return isEditMode
          ? <span className="text-xs text-gray-500 font-medium">Auto-save on</span>
          : null;
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-200 ${isVisible ? 'opacity-50' : 'opacity-0'}`}
        onClick={handleClose}
      />

      {/* Slide-over panel */}
      <div
        ref={panelRef}
        className={`ml-auto relative flex flex-col h-full bg-dark-card border-l border-dark-border shadow-2xl transition-transform duration-200 ease-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: '90vw', maxWidth: '1200px' }}
      >
        {/* Sticky header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 bg-dark-card border-b border-dark-border">
          <div className="flex items-center gap-4">
            <button
              onClick={handleClose}
              className="text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
            >
              &larr; Back to Team
            </button>
            <h2 className="text-lg font-bold text-dark-text">
              {isEditMode ? 'Edit Position' : 'New Position'}
            </h2>
            {renderStatusIndicator()}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="px-3 py-1.5 bg-dark-bg border border-dark-border hover:border-indigo-500 text-gray-300 hover:text-white text-sm font-medium rounded-lg transition-colors"
            >
              Import JD
            </button>
            {isEditMode && (
              <>
                <button
                  onClick={handleExportClick}
                  className="px-3 py-1.5 bg-dark-bg border border-dark-border hover:border-indigo-500 text-gray-300 hover:text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Export JD
                </button>
                <button
                  onClick={() => setShowInfographicModal(true)}
                  className="px-3 py-1.5 bg-dark-bg border border-dark-border hover:border-indigo-500 text-gray-300 hover:text-white text-sm font-medium rounded-lg transition-colors"
                  title={hasInfographic ? 'View stored infographic' : 'Generate role infographic with Gemini'}
                >
                  {hasInfographic ? 'View infographic' : 'Generate infographic'}
                </button>
              </>
            )}
            <button
              onClick={handleClose}
              className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable form content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* SECTION: BASIC INFO */}
          <section>
            <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-4">Basic Info</h3>
            <div className="grid grid-cols-12 gap-4">
              {/* Role */}
              <div className="col-span-7">
                <label className="block text-xs text-gray-400 mb-1">Role / Job Title *</label>
                <input
                  type="text"
                  value={role}
                  onChange={e => { setRole(e.target.value); hasUnsavedFields.current = true; if (positionIdRef.current) { scheduleAutoSave({ role: e.target.value }); scheduleAnalysis('role'); } }}
                  onBlur={handleRoleBlur}
                  placeholder="e.g. Senior Frontend Developer"
                  className={`w-full bg-dark-bg border rounded-lg px-3 py-2 text-sm text-dark-text placeholder:text-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none ${
                    !role.trim() ? 'border-red-500' : 'border-dark-border'
                  }`}
                />
              </div>
              {/* Count */}
              <div className="col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Count</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={countNeeded}
                  onChange={e => updateField('count_needed', parseInt(e.target.value) || 1, setCountNeeded)}
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-dark-text focus:border-indigo-500 outline-none"
                />
              </div>
              {/* Priority chips */}
              <div className="col-span-3">
                <label className="block text-xs text-gray-400 mb-1">Priority</label>
                <div className="flex gap-1">
                  {PRIORITY_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => updateField('priority', opt.value, setPriority)}
                      className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                        priority === opt.value
                          ? opt.color
                          : 'bg-dark-bg border border-dark-border text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mt-4">
              <label className="block text-xs text-gray-400 mb-1">Description</label>
              <textarea
                value={description}
                onChange={e => updateField('description', e.target.value, setDescription)}
                placeholder="Describe the role, its responsibilities, and expectations..."
                rows={3}
                className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-dark-text placeholder:text-gray-500 focus:border-indigo-500 outline-none resize-y"
              />
              {onAIFill && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleAIMagic}
                    disabled={(!role.trim() && !description.trim()) || aiFillingFields}
                    className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {aiFillingFields ? '✨ Working...' : '✨ AI Magic'}
                  </button>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    Fills empty fields from role & description using team and project context.
                  </span>
                </div>
              )}
            </div>

            {/* Status, Employment Type, Allocation */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Status</label>
                <select
                  value={status}
                  onChange={e => updateField('status', e.target.value as PositionStatus, setStatus)}
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-dark-text focus:border-indigo-500 outline-none"
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Employment Type</label>
                <select
                  value={employmentType}
                  onChange={e => {
                    const val = e.target.value;
                    updateField('employment_type', val, setEmploymentType);
                    if (val === 'full_time') {
                      updateField('allocation_percentage', 100, setAllocationPercentage);
                    }
                  }}
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-dark-text focus:border-indigo-500 outline-none"
                >
                  <option value="">Not specified</option>
                  {EMPLOYMENT_TYPES.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs mb-1 ${employmentType === 'full_time' ? 'text-gray-600' : 'text-gray-400'}`}>
                  Allocation (FTE): {allocationPercentage}%
                  {employmentType === 'full_time' && <span className="ml-1 text-gray-600 italic">(fixed)</span>}
                </label>
                <input
                  type="range"
                  min={10}
                  max={100}
                  step={10}
                  value={allocationPercentage}
                  disabled={employmentType === 'full_time'}
                  onChange={e => updateField('allocation_percentage', parseInt(e.target.value), setAllocationPercentage)}
                  className={`w-full h-2 mt-1 ${employmentType === 'full_time' ? 'opacity-40 cursor-not-allowed' : 'accent-indigo-500'}`}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                  <span>10%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </section>

          <hr className="border-dark-border border-dashed" />

          {/* SECTION: LOCATION & RATE */}
          <section>
            <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-4">Location &amp; Rate</h3>
            <div className="grid grid-cols-3 gap-4">
              {/* Work Model */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Work Model</label>
                <div className="flex gap-1">
                  {WORK_MODEL_OPTIONS.map(model => (
                    <button
                      key={model}
                      onClick={() => { const next = locationModel === model ? '' : model; updateField('location_model', next, setLocationModel); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        locationModel === model
                          ? 'bg-indigo-600 text-white'
                          : 'bg-dark-bg border border-dark-border text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      {model.charAt(0).toUpperCase() + model.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              {/* Location */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Location</label>
                <input
                  type="text"
                  value={location}
                  onChange={e => updateField('location', e.target.value, setLocation)}
                  placeholder="e.g. Prague, CZ"
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-dark-text placeholder:text-gray-500 focus:border-indigo-500 outline-none"
                />
              </div>
              {/* Rate Country */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Rate Country</label>
                <select
                  value={rateCountry}
                  onChange={e => updateField('rate_country', e.target.value, setRateCountry)}
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-dark-text focus:border-indigo-500 outline-none"
                >
                  <option value="">Not specified</option>
                  {RATE_COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <hr className="border-dark-border border-dashed" />

          {/* SECTION: REQUIREMENTS */}
          <section>
            <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-4">Requirements</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Experience Level</label>
                <select
                  value={experienceLevel}
                  onChange={e => updateField('experience_level', e.target.value, setExperienceLevel)}
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-dark-text focus:border-indigo-500 outline-none"
                >
                  <option value="">Not specified</option>
                  {EXPERIENCE_LEVELS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Management Level</label>
                <select
                  value={managementLevel}
                  onChange={e => updateField('management_level', e.target.value, setManagementLevel)}
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-dark-text focus:border-indigo-500 outline-none"
                >
                  <option value="">Not specified</option>
                  {MANAGEMENT_LEVELS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Education Level</label>
                <select
                  value={educationLevel}
                  onChange={e => updateField('education_level', e.target.value, setEducationLevel)}
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-dark-text focus:border-indigo-500 outline-none"
                >
                  <option value="">Not specified</option>
                  {EDUCATION_LEVELS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Edu. Importance</label>
                <select
                  value={educationImportance}
                  onChange={e => updateField('education_importance', e.target.value, setEducationImportance)}
                  className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-dark-text focus:border-indigo-500 outline-none"
                >
                  <option value="">Not specified</option>
                  {EDUCATION_IMPORTANCE.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <hr className="border-dark-border border-dashed" />

          {/* SECTION: SKILLS */}
          <section>
            <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-4">Skills</h3>

            {/* Required Skills */}
            <div className="mb-4">
              <label className="block text-xs text-gray-400 mb-1">Required Skills</label>
              <div className="flex flex-wrap gap-1.5 p-2 bg-dark-bg border border-dark-border rounded-lg min-h-[40px]">
                {skillsRequired.map((skill, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-600 text-white text-xs rounded-full">
                    {skill}
                    <button onClick={() => removeSkill('required', i)} className="hover:text-red-300 ml-0.5">&times;</button>
                  </span>
                ))}
                <input
                  type="text"
                  value={newRequiredSkill}
                  onChange={e => setNewRequiredSkill(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); addSkill('required', newRequiredSkill); }
                  }}
                  placeholder="+ Add skill"
                  className="bg-transparent outline-none text-sm text-dark-text placeholder:text-gray-500 min-w-[100px] flex-1"
                />
              </div>
            </div>

            {/* Nice-to-Have Skills */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Nice-to-Have Skills</label>
              <div className="flex flex-wrap gap-1.5 p-2 bg-dark-bg border border-dark-border rounded-lg min-h-[40px]">
                {skillsNiceToHave.map((skill, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-600 text-gray-200 text-xs rounded-full">
                    {skill}
                    <button onClick={() => removeSkill('nice', i)} className="hover:text-red-300 ml-0.5">&times;</button>
                  </span>
                ))}
                <input
                  type="text"
                  value={newNiceSkill}
                  onChange={e => setNewNiceSkill(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); addSkill('nice', newNiceSkill); }
                  }}
                  placeholder="+ Add skill"
                  className="bg-transparent outline-none text-sm text-dark-text placeholder:text-gray-500 min-w-[100px] flex-1"
                />
              </div>
            </div>
          </section>

          <hr className="border-dark-border border-dashed" />

          {/* SECTION: RESPONSIBILITIES */}
          <section>
            <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-4">Responsibilities</h3>
            <div className="bg-dark-bg border border-dark-border rounded-lg p-3 space-y-2">
              {responsibilities.map((resp, i) => (
                <div key={i} className="flex items-start gap-2 group">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 flex-shrink-0" />
                  <span className="text-sm text-dark-text flex-1">{resp}</span>
                  <button
                    onClick={() => removeResponsibility(i)}
                    className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity text-sm"
                  >
                    &times;
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400/40 flex-shrink-0" />
                <input
                  type="text"
                  value={newResponsibility}
                  onChange={e => setNewResponsibility(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); addResponsibility(newResponsibility); }
                  }}
                  placeholder="+ Add responsibility"
                  className="bg-transparent outline-none text-sm text-indigo-400 placeholder:text-indigo-400/50 flex-1"
                />
              </div>
            </div>
          </section>

          <hr className="border-dark-border border-dashed" />

          {/* SECTION: AI ANALYSIS */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-widest">AI Analysis</h3>
              {analysisLoading && (
                <span className="flex items-center gap-1.5 text-xs text-purple-400 animate-pulse">
                  <span className="inline-block w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                  Analyzing...
                </span>
              )}
            </div>
            <div className={`bg-dark-bg border border-purple-500/30 border-dashed rounded-lg p-4 min-h-[80px] transition-opacity ${analysisLoading ? 'opacity-60' : ''}`}>
              {aiAnalysis ? (
                <p className="text-sm text-purple-300 leading-relaxed whitespace-pre-line">{aiAnalysis}</p>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  {isEditMode
                    ? 'Analysis will generate automatically as you fill in position details.'
                    : 'AI analysis will be available after the position is created.'}
                </p>
              )}
            </div>
          </section>

          {/* SECTION: CANDIDATES (read-only) */}
          {isEditMode && candidates.length > 0 && (
            <>
              <hr className="border-dark-border border-dashed" />
              <section>
                <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-4">
                  Candidates ({candidates.filter(c => c.status === 'selected').length}/{countNeeded} filled)
                </h3>
                <div className="flex flex-wrap gap-3">
                  {candidates.map((c: TeamCandidate) => {
                    const initials = (c.profile?.name || 'U')
                      .split(' ')
                      .map(w => w[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);
                    const matchScore = c.scores
                      ? Math.min(100, Math.round((c.scores.skillMatch * 0.4 + c.scores.semanticMatch * 0.6) + c.scores.teamFitBonus))
                      : c.match_score;
                    const scoreColor = matchScore && matchScore >= 80 ? 'text-green-400' :
                      matchScore && matchScore >= 60 ? 'text-yellow-400' : 'text-blue-400';
                    const borderColor = c.status === 'selected' ? 'border-green-500' : 'border-dark-border';

                    return (
                      <div key={c.person_id} className={`flex items-center gap-2 p-2.5 bg-dark-bg border ${borderColor} rounded-lg min-w-[180px]`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white ${
                          c.status === 'selected' ? 'bg-green-600' : 'bg-blue-600'
                        }`}>
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-dark-text truncate">{c.profile?.name || 'Unknown'}</div>
                          <div className="text-xs text-gray-400 truncate">{c.profile?.title || ''}</div>
                          {matchScore !== undefined && matchScore !== null && (
                            <div className={`text-xs font-medium ${scoreColor}`}>{matchScore}% match</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}

          {/* Bottom spacer */}
          <div className="h-6" />
        </div>
      </div>

      {/* JD Export Modal */}
      {showExportModal && positionId && (
        <JDExportModal
          teamId={teamIdForPosition}
          positionId={positionId}
          roleName={role}
          hasInfographic={hasInfographic}
          onClose={() => setShowExportModal(false)}
          onInfographicGenerated={() => { setHasInfographic(true); if (positionId) onInfographicGenerated?.(positionId); }}
        />
      )}

      {/* Infographic Modal (Nano Banana / Gemini image) */}
      {showInfographicModal && positionId && (
        <InfographicModal
          teamId={teamIdForPosition}
          positionId={positionId}
          roleName={role}
          existingInfographic={
            position?.infographic_base64 && position.infographic_base64.length > 256
              ? { base64: position.infographic_base64, mimeType: position.infographic_mime_type || 'image/png' }
              : null
          }
          onClose={() => setShowInfographicModal(false)}
          onGenerated={() => { setHasInfographic(true); if (positionId) onInfographicGenerated?.(positionId); }}
        />
      )}

      {/* JD Import Modal */}
      {showImportModal && (
        <JDImportModal
          teamId={resolvedTeamId}
          getTeamId={getValidTeamId}
          isEditMode={!!positionIdRef.current}
          onApply={handleApplyImport}
          onCreateNew={positionIdRef.current ? handleCreateNewFromImport : undefined}
          onClose={() => setShowImportModal(false)}
          onDrivePick={pickFileFromDrive}
        />
      )}
    </div>
  );
};

export default PositionEditDialog;
